
import { defineComponent, computed, watch, ref, PropType } from 'vue'
import { useRouter } from 'vue-router'

import JrButton from '@/components/jr-parts/JrButton.vue'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import { MessageTypes } from '@/constants/messageTypes'
import {
  P_AS_PRE_EXPLAIN,
  P_JD_DONE,
  P_DD_CONFIRM,
  P_DD_RECEIPT,
  P_AS_SEND_DRAFT,
  P_AS_CHECK_CONFIRMATION,
  P_AS_COMPLITE_CONFIRMATION,
} from '@/constants/statuses'
import { CreateRequest } from '@/requests/proximityFile'
import { ProximityFile } from '@/api'
import { MESSAGE_TYPE_ERROR, addMessageQueue, MESSAGE_TYPE_TOAST } from '@/plugins/messageQueue'
import MessageService from '@/services/message'

type PageType = 'applicant' | 'discussion'

/**
 * メッセージ毎ボタンの定義
 */
interface ReceiveAction {
  name: string // ボタン表示名
  action: () => void // 動作
  disabled: boolean
}

/**
 * ステータスチェック
 * receiveのステータス未満なら実行可能
 */
const canActionStatus = (receive: number | null | undefined, expect: number): boolean => {
  if (receive == null) {
    return false
  }
  return receive < expect
}

/**
 * 受信時の各種ボタン
 */
export default defineComponent({
  name: 'ReceiveActions',
  components: {
    JrButton,
  },
  props: {
    // メッセージ種別
    messageType: {
      type: Number,
      required: true,
    },
    // 申請基本ID
    requestHeaderId: {
      type: Number,
      required: true,
    },
    // メッセージID
    messageId: {
      type: Number,
      required: true,
    },
    // 添付ファイルId
    fileId: {
      type: Number,
      required: false,
      default: null,
    },
    // JRか協議者側か
    type: {
      type: String as PropType<PageType>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter()
    const requestHeaderRequest = ref<RequestHeaderReadRequest>()
    // 申請基本IDが変更されれば情報を取りに行く
    watch(
      () => props.requestHeaderId,
      async (id) => {
        requestHeaderRequest.value = undefined
        if (Number.isNaN(id)) {
          return
        }
        const request = new RequestHeaderReadRequest(id)
        await request.exec()
        if (request.success) {
          // 取得に成功した場合、クラスを保持する
          requestHeaderRequest.value = request
        }
      },
      { immediate: true }
    )

    /**
     * 返信のroute定義を作成する
     */
    const messageReplyRouteParam = (name: string, toType: number) => {
      return {
        name: name,
        params: {
          request_header_id: props.requestHeaderId,
          message_type: toType,
          action: 'reply',
          message_id: props.messageId,
        },
      }
    }

    /**
     * 各種ボタンを返す
     */
    const receiveActions = computed((): ReceiveAction[] => {
      if (requestHeaderRequest.value == null) {
        // 申請基本情報が無ければ何もしない
        return []
      }
      // 申請者
      if (props.messageType === MessageTypes.P_AS_PRE_EXPLAIN) {
        // 回答案事前説明
        return [
          {
            name: '異議なし',
            action: () => {
              router.push(messageReplyRouteParam('ApplicantNormalMessage', MessageTypes.P_AS_PRE_EXPLAIN_OK))
            },
            disabled: !canActionStatus(
              requestHeaderRequest.value.getProximity().proximity.answer_status,
              P_AS_PRE_EXPLAIN
            ),
          },
        ]
        // JR側
      } else if (props.messageType === MessageTypes.P_JD_ASK) {
        // 要否判断伺い
        return [
          {
            name: '協議要否判断',
            action: () => {
              router.push({
                name: 'ProximityJudge',
                params: {
                  request_header_id: props.requestHeaderId,
                },
              })
            },
            disabled: !canActionStatus(
              requestHeaderRequest.value.getProximity().proximity.judge_discussion_status,
              P_JD_DONE
            ),
          },
        ]
      } else if (props.messageType === MessageTypes.P_DD_REQUEST_CONFIRM) {
        // 協議書確認
        return [
          {
            name: '内容確認通知',
            action: () => {
              router.push(messageReplyRouteParam('NormalMessage', MessageTypes.P_DD_CONFIRM_NOTICE))
            },
            disabled: !canActionStatus(
              requestHeaderRequest.value.getProximity().proximity.discussion_document_status,
              P_DD_CONFIRM
            ),
          },
        ]
      } else if (props.messageType === MessageTypes.P_DD_CONFIRM_NOTICE) {
        // 協議書確認通知
        return [
          {
            name: '受領',
            action: () => {
              router.push(messageReplyRouteParam('NormalMessage', MessageTypes.P_DD_RECEIPT))
            },
            disabled: !canActionStatus(
              requestHeaderRequest.value.getProximity().proximity.discussion_document_status,
              P_DD_RECEIPT
            ),
          },
        ]
      } else if (props.messageType === MessageTypes.P_AS_ASK) {
        // 回答案伺い
        return [
          {
            name: '回答案送付(事前説明)',
            action: () => {
              router.push(messageReplyRouteParam('NormalMessage', MessageTypes.P_AS_PRE_EXPLAIN))
            },
            disabled: !canActionStatus(
              requestHeaderRequest.value.getProximity().proximity.answer_status,
              P_AS_SEND_DRAFT
            ),
          },
        ]
      } else if (props.messageType === MessageTypes.P_AS_ANSWER_RECEIPT_CONFIRM) {
        // 回答書請書確認
        return [
          {
            name: '内容確認通知',
            action: () => {
              router.push(messageReplyRouteParam('NormalMessage', MessageTypes.P_AS_ANSWER_RECEIPT_NOTICE))
            },
            disabled: !canActionStatus(
              requestHeaderRequest.value.getProximity().proximity.answer_status,
              P_AS_CHECK_CONFIRMATION
            ),
          },
        ]
      } else if (props.messageType === MessageTypes.P_AS_ANSWER_RECEIPT_NOTICE) {
        // 回答書請書確認通知
        // 回答請書に添付ファイルを送付
        const proximityId = requestHeaderRequest.value.getProximity().proximity.proximity_id
        return [
          {
            name: '受領',
            action: async () => {
              if (proximityId && props.fileId) {
                const fileInfo: ProximityFile = {
                  ['answer_confirmation_file']: props.fileId,
                }
                const request = new CreateRequest(proximityId, fileInfo)
                await request.exec()
                if (request.success) {
                  // 成功時、受領のトーストを出す
                  addMessageQueue(
                    MESSAGE_TYPE_TOAST,
                    MessageService.bind(MessageService.codes.ANSWER_CONFIRMATION_RECEIVED, {})
                  )
                  // 送信画面へ遷移
                  router.push(messageReplyRouteParam('NormalMessage', MessageTypes.P_AS_RECEIPT_NOTICE))
                } else {
                  // 失敗した場合、エラーメッセージを出す
                  addMessageQueue(
                    MESSAGE_TYPE_ERROR,
                    MessageService.bind(MessageService.codes.FILE_SET_ERROR, {
                      msg: '詳細画面より回答請書をアップロードしてください。',
                    })
                  )
                  // 詳細画面へ戻る
                  if (props.requestHeaderId) {
                    router.push({
                      name: 'ProximityDetail',
                      params: {
                        request_header_id: props.requestHeaderId,
                      },
                    })
                  }
                }
              } else {
                throw new Error('回答請書を受領できません。不正な申請です。')
              }
            },
            disabled: !canActionStatus(
              requestHeaderRequest.value.getProximity().proximity.answer_status,
              P_AS_COMPLITE_CONFIRMATION
            ),
          },
        ]
      }
      return []
    })

    const applicantButtonClass = computed(() => {
      let btnSize = null
      if (props.type === 'applicant') {
        btnSize = 'largest-btn-text'
      }
      return btnSize
    })

    return { receiveActions, applicantButtonClass }
  },
})
